import { memo, useState } from "react";
import { useTranslate } from "helpers";
import { ListingLocation } from "services/api/sales/listings/types";
import { AddressMap } from "ui/components/Map/AddressMap";
import ListingLocationEditCard from "./ListingLocationEditCard";

interface ListingLocationCardProps {
  location: ListingLocation;
  onLocationChange?: (location: ListingLocation) => void;
  /** Ugniježđeno u postojeću sekciju — bez vlastitog naslova i okvira. */
  embedded?: boolean;
}

const ListingLocationCard: React.FC<ListingLocationCardProps> = ({ location, onLocationChange, embedded }) => {
  const { t } = useTranslate();
  const [showModal, setShowModal] = useState(false);

  const mapPosition = location.latitude && location.longitude
    ? { lat: location.latitude, lng: location.longitude }
    : undefined;

  const handleOpenModal = () => setShowModal(true);

  const handleSuccess = (updatedLocation: ListingLocation) => {
    onLocationChange?.(updatedLocation);
    setShowModal(false);
  };

  return (
    <>
      <div className={embedded ? "" : "border-b border-slate-200 pt-4 px-4"}>
        {!embedded && (
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">
            {t("admin.sales.listing.detail.location")}
          </div>
        )}

        {/* Preview mapa s pinom */}
        <div
          className="mb-4 rounded-sm overflow-hidden cursor-pointer border border-slate-200 relative"
          style={{ height: "200px" }}
        >
          <AddressMap
            initialPosition={mapPosition}
            hideAutocomplete
            formData={{
              country: location.country ?? undefined,
              city: location.city ?? undefined,
              line1: location.line1 ?? undefined,
              postalCode: location.postalCode ?? undefined,
            }}
          />
          <div className="absolute inset-0 z-20" onClick={handleOpenModal} />
        </div>

        {/* Adresa kartice */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm">
            <div className="text-sm text-slate-600">
              {t("admin.sales.listing.detail.location.address")}
            </div>
            <div className="text-md font-medium text-slate-800 truncate">
              {location.line1 ?? "-"}
            </div>
          </div>
          <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm">
            <div className="text-sm text-slate-600">
              {t("admin.sales.listing.detail.location.city")}
            </div>
            <div className="text-md font-medium text-slate-800 truncate">
              {location.city ?? "-"}
            </div>
          </div>
          <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm">
            <div className="text-sm text-slate-600">
              {t("admin.sales.listing.detail.location.postalcode")}
            </div>
            <div className="text-md font-medium text-slate-800 truncate">
              {location.postalCode ?? "-"}
            </div>
          </div>
          <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm">
            <div className="text-sm text-slate-600">
              {t("admin.sales.listing.detail.location.country")}
            </div>
            <div className="text-md font-medium text-slate-800 truncate">
              {location.country ?? "-"}
            </div>
          </div>
        </div>
      </div>

      <ListingLocationEditCard
        show={showModal}
        location={location}
        onSuccess={handleSuccess}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};

export default memo(ListingLocationCard);
